import { Component } from '@theme/component';

const MOBILE_BP = 768;
const DPR = window.devicePixelRatio || 1;
const RINGS = 5;
const MAX = 10;
const ANIM_DUR = 550;

const TABS = [
  { key: 'occasion', label: 'Occasion' },
  { key: 'season', label: 'Season' },
  { key: 'audience', label: 'Audience' },
  { key: 'character', label: 'Character' },
  { key: 'type', label: 'Type' },
];
const FIRST_KEY = TABS[0].key;
const TAB_KEYS = TABS.map((t) => t.key);

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Classification PDP section, ported line-for-line from the theme's real original
 * per-product template (the same `<div id="pp-root">` blob confirmed live on other
 * Verve & Vesper products) rather than a redesign. Tabs and dots are built at runtime
 * from a TABS array exactly as the original does; the radar canvas measures every tab's
 * label extents up front so the frame doesn't resize when switching tabs, and animates
 * between each tab's own axes/values via requestAnimationFrame. Only DOM lookups are
 * scoped to `this` instead of `document.getElementById` so multiple instances don't
 * collide — the drawing/animation logic itself is unchanged.
 * @extends {Component}
 */
class ClassificationComponent extends Component {
  requiredRefs = ['tabs', 'descArea', 'radar', 'dots', 'swipeHint', 'nav', 'navPrev', 'navNext', 'navLabel', 'data'];

  connectedCallback() {
    super.connectedCallback();

    this._radarData = JSON.parse(this.refs.data.textContent);
    this._radarCtx = this.refs.radar.getContext('2d');
    this._currentKey = FIRST_KEY;
    this._currentData = this._radarData[FIRST_KEY].data.slice();
    this._targetData = this._radarData[FIRST_KEY].data.slice();
    this._currentLabels = this._radarData[FIRST_KEY].labels.slice();
    this._animStart = null;
    this._rafId = null;
    this._swipeHintDismissed = false;

    this._buildTabs();
    this._buildDots();
    this._updateNavVisibility();

    this.refs.navPrev.addEventListener('click', () => {
      const idx = TAB_KEYS.indexOf(this._currentKey);
      if (idx > 0) this._switchTab(TAB_KEYS[idx - 1]);
    });
    this.refs.navNext.addEventListener('click', () => {
      const idx = TAB_KEYS.indexOf(this._currentKey);
      if (idx < TAB_KEYS.length - 1) this._switchTab(TAB_KEYS[idx + 1]);
    });

    let swipeStartX = 0;
    let swipeStartY = 0;
    this.refs.radar.addEventListener('touchstart', (e) => {
      swipeStartX = e.touches[0].clientX;
      swipeStartY = e.touches[0].clientY;
    }, { passive: true });
    this.refs.radar.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - swipeStartX;
      const dy = e.changedTouches[0].clientY - swipeStartY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        const idx = TAB_KEYS.indexOf(this._currentKey);
        let switched = false;
        if (dx < 0 && idx < TAB_KEYS.length - 1) {
          this._switchTab(TAB_KEYS[idx + 1]);
          switched = true;
        } else if (dx > 0 && idx > 0) {
          this._switchTab(TAB_KEYS[idx - 1]);
          switched = true;
        }
        if (switched) this._dismissSwipeHint();
      }
    }, { passive: true });

    this._resizeHandler = () => {
      const newW = window.innerWidth;
      if (Math.abs(newW - this._lastW) < 10) return;
      this._lastW = newW;
      this._buildTabs();
      this._updateNavVisibility();
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      this._animStart = null;
      this._initRadarCanvas();
      this._setDescriptor(this._currentKey, false);
      this._setRadar(this._currentKey, false);
    };
    this._lastW = window.innerWidth;
    window.addEventListener('resize', this._resizeHandler);

    const init = () => {
      this._initRadarCanvas();
      this._setDescriptor(FIRST_KEY, false);
      this._setRadar(FIRST_KEY, false);
      this._updateNavButtons();
    };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(init);
    else window.setTimeout(init, 500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this._resizeHandler);
    if (this._rafId) cancelAnimationFrame(this._rafId);
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BP;
  }

  /** Dynamic font-fit tab buttons, built from TABS exactly as the original does. */
  _buildTabs() {
    const availW = this.refs.tabs.offsetWidth || window.innerWidth - 40;
    const tabW = Math.floor((availW - 4 * 4) / 5);
    const tc = document.createElement('canvas');
    const tcx = tc.getContext('2d');
    let fontSize = 11;
    while (fontSize > 6) {
      tcx.font = `300 ${fontSize}px "Jost", sans-serif`;
      let maxW = 0;
      TABS.forEach((t) => {
        const w = tcx.measureText(t.label.toUpperCase()).width;
        if (w > maxW) maxW = w;
      });
      if (maxW <= tabW - 10) break;
      fontSize -= 0.5;
    }
    fontSize = Math.floor(fontSize);

    this.refs.tabs.innerHTML = '';
    TABS.forEach((t, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'classification__tab' + (i === 0 ? ' is-active' : '');
      btn.dataset.k = t.key;
      btn.textContent = t.label;
      btn.style.fontSize = `${fontSize}px`;
      btn.style.letterSpacing = '1px';
      btn.addEventListener('click', () => this._switchTab(t.key));
      this.refs.tabs.appendChild(btn);
    });
  }

  _buildDots() {
    this.refs.dots.innerHTML = '';
    TABS.forEach((t, i) => {
      const d = document.createElement('div');
      d.className = 'classification__dot' + (i === 0 ? ' is-active' : '');
      d.dataset.k = t.key;
      this.refs.dots.appendChild(d);
    });
  }

  _updateDots(key) {
    this.querySelectorAll('.classification__dot').forEach((d) => d.classList.toggle('is-active', d.dataset.k === key));
  }

  _updateNavVisibility() {
    this.refs.swipeHint.style.display = this._isMobile() ? 'flex' : 'none';
    this.refs.nav.style.display = this._isMobile() ? 'none' : 'flex';
  }

  _updateNavButtons() {
    const idx = TAB_KEYS.indexOf(this._currentKey);
    this.refs.navPrev.disabled = idx === 0;
    this.refs.navNext.disabled = idx === TAB_KEYS.length - 1;
    this.refs.navLabel.textContent = TABS[idx].label;
  }

  _dismissSwipeHint() {
    if (this._swipeHintDismissed) return;
    this._swipeHintDismissed = true;
    this.refs.swipeHint.classList.add('is-hidden');
  }

  /** Measures every tab's label extents up front so the canvas frame is stable across tab switches. */
  _initRadarCanvas() {
    const hpad = 20;
    let availW = this.refs.tabs.offsetWidth - hpad * 2;
    if (availW < 200) availW = window.innerWidth - hpad * 2;
    this._labelFontSize = Math.max(9, Math.min(11, Math.round(availW * 0.028)));
    this._labelGap = Math.max(10, Math.round(availW * 0.032));

    const ctx = this._radarCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.font = `300 ${this._labelFontSize}px "Jost", sans-serif`;
    let padL = 0;
    let padR = 0;
    let padT = this._labelFontSize + 6;
    let padB = this._labelFontSize + 6;
    Object.values(this._radarData).forEach((tab) => {
      const n = tab.labels.length;
      tab.labels.forEach((l, i) => {
        const a = Math.PI * 2 * (i / n) - Math.PI / 2;
        const cos = Math.cos(a);
        const w = ctx.measureText(l.toUpperCase()).width;
        if (cos > 0.15) {
          if (w + this._labelGap + 6 > padR) padR = w + this._labelGap + 6;
        } else if (cos < -0.15) {
          if (w + this._labelGap + 6 > padL) padL = w + this._labelGap + 6;
        } else {
          if (w / 2 + this._labelGap + 6 > padT) padT = w / 2 + this._labelGap + 6;
          if (w / 2 + this._labelGap + 6 > padB) padB = w / 2 + this._labelGap + 6;
        }
      });
    });
    const padSide = Math.max(padL, padR);
    this._radius = (availW - padSide * 2) / 2;
    this._cssW = Math.ceil(padSide * 2 + this._radius * 2);
    this._cssH = Math.ceil(padT + this._radius * 2 + padB);
    this._cx = this._cssW / 2;
    this._cy = padT + this._radius;
    this.refs.radar.style.width = `${this._cssW}px`;
    this.refs.radar.style.height = `${this._cssH}px`;
    this.refs.radar.width = Math.ceil(this._cssW * DPR);
    this.refs.radar.height = Math.ceil(this._cssH * DPR);
    ctx.scale(DPR, DPR);
  }

  _drawRadarFrame(data, labels) {
    const ctx = this._radarCtx;
    const { cx, cy, radius: R } = { cx: this._cx, cy: this._cy, radius: this._radius };
    ctx.clearRect(0, 0, this._cssW, this._cssH);
    const n = labels.length;
    const ang = (i) => Math.PI * 2 * (i / n) - Math.PI / 2;
    const pt = (i, val) => {
      const a = ang(i);
      const r = R * (val / MAX);
      return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
    };

    for (let ring = 1; ring <= RINGS; ring++) {
      const r = R * (ring / RINGS);
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const a = ang(i);
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = 'rgba(184,166,140,0.28)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
    for (let i = 0; i < n; i++) {
      const a = ang(i);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + R * Math.cos(a), cy + R * Math.sin(a));
      ctx.strokeStyle = 'rgba(184,166,140,0.2)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const p = pt(i, data[i]);
      i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(154,132,92,0.10)';
    ctx.fill();
    ctx.strokeStyle = '#9A845C';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    for (let i = 0; i < n; i++) {
      const p = pt(i, data[i]);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = '#9A845C';
      ctx.fill();
      ctx.strokeStyle = '#F3EFE8';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    ctx.font = `300 ${this._labelFontSize}px "Jost", sans-serif`;
    ctx.fillStyle = '#4B443D';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < n; i++) {
      const a = ang(i);
      const cos = Math.cos(a);
      const lx = cx + (R + this._labelGap) * Math.cos(a);
      const ly = cy + (R + this._labelGap) * Math.sin(a);
      ctx.textAlign = cos > 0.15 ? 'left' : cos < -0.15 ? 'right' : 'center';
      ctx.fillText(labels[i].toUpperCase(), lx, ly);
    }
  }

  _radarAnimLoop(ts) {
    if (!this._animStart) this._animStart = ts;
    const t = Math.min((ts - this._animStart) / ANIM_DUR, 1);
    const ease = easeInOut(t);
    this._drawRadarFrame(this._currentData.map((v, i) => lerp(v, this._targetData[i], ease)), this._currentLabels);
    if (t < 1) {
      this._rafId = requestAnimationFrame((ts2) => this._radarAnimLoop(ts2));
    } else {
      this._currentData = this._targetData.slice();
      this._animStart = null;
      this._rafId = null;
    }
  }

  _setRadar(key, anim) {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
      this._animStart = null;
    }
    this._currentKey = key;
    this._currentLabels = this._radarData[key].labels.slice();
    if (anim) {
      this._targetData = this._radarData[key].data.slice();
      this._rafId = requestAnimationFrame((ts) => this._radarAnimLoop(ts));
    } else {
      this._currentData = this._radarData[key].data.slice();
      this._targetData = this._currentData.slice();
      this._drawRadarFrame(this._currentData, this._currentLabels);
    }
  }

  _setDescriptor(key, anim) {
    const d = this._radarData[key];
    const descArea = this.refs.descArea;

    if (key === 'audience') {
      const html = `<div class="classification__spectrum-wrap">
        <div class="classification__spectrum-label-row"><span>Masculine</span><span>Unisex</span><span>Feminine</span></div>
        <div class="classification__spectrum-track"><div class="classification__spectrum-dot" style="left:${d.position}%"></div></div>
        <div class="classification__spectrum-title">${d.title}</div>
        <div class="classification__spectrum-sub">${d.line}</div>
      </div>`;
      descArea.style.transition = 'opacity 0.3s';
      descArea.style.opacity = '0';
      window.setTimeout(() => {
        descArea.innerHTML = html;
        descArea.style.opacity = '1';
      }, anim ? 300 : 0);
      return;
    }

    const hasDesc = descArea.querySelector('.classification__desc-wrap');
    if (!hasDesc) {
      const html = `<div class="classification__desc-wrap">
        <div class="classification__compound" data-el="compound">${d.compound}</div>
        <div class="classification__line" data-el="line">${d.line}</div>
      </div>`;
      descArea.style.transition = 'opacity 0.3s';
      descArea.style.opacity = '0';
      window.setTimeout(() => {
        descArea.innerHTML = html;
        descArea.style.opacity = '1';
      }, anim ? 300 : 0);
    } else {
      const compound = descArea.querySelector('[data-el="compound"]');
      const line = descArea.querySelector('[data-el="line"]');
      if (compound && line) {
        compound.classList.add('is-fading');
        line.classList.add('is-fading');
        window.setTimeout(() => {
          compound.textContent = d.compound;
          line.textContent = d.line;
          compound.classList.remove('is-fading');
          line.classList.remove('is-fading');
        }, 320);
      }
    }
  }

  _switchTab(key) {
    if (key === this._currentKey) return;
    this._currentKey = key;
    this.querySelectorAll('.classification__tab').forEach((x) => x.classList.toggle('is-active', x.dataset.k === key));
    this._updateDots(key);
    this._updateNavButtons();
    this._setDescriptor(key, true);
    this._setRadar(key, true);
  }
}

if (!customElements.get('classification-component')) {
  customElements.define('classification-component', ClassificationComponent);
}
